/**
 * MetricNotice — Aviso padrao CROSS M3 para metricas descontinuadas ou atualizadas
 * por suas fontes de origem.
 *
 * Uso por id (recomendado — tudo centralizado em metric-notices.ts):
 *   <MetricNotice id="instagram.video_reach" />
 *
 * Uso direto (quando nao ha entrada no registro):
 *   <MetricNotice kind="discontinued" titleKey="..." bodyKey="..." />
 *
 * Tom: informativo, nao de erro. Linguagem amigavel de dono de negocio.
 * Icone Material Symbol: info (updated) | update (discontinued).
 * Acessibilidade AA: role="note", contraste garantido pelos tokens CROSS M3.
 * i18n: textos buscados via t() — zero string hardcoded.
 * Zero emoji.
 *
 * F3 — 2026-06-09
 */

import { t } from '../lib/i18n';
import { metricNotices, type MetricNoticeKind } from '../lib/metric-notices';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface MetricNoticeByIdProps {
  /** Id da metrica no registro metric-notices.ts (ex: 'instagram.video_reach') */
  readonly id: string;
  readonly kind?: never;
  readonly titleKey?: never;
  readonly bodyKey?: never;
}

interface MetricNoticeDirectProps {
  readonly id?: never;
  /** Tipo de aviso: descontinuado ou atualizado */
  readonly kind: MetricNoticeKind;
  /** Chave i18n para o titulo */
  readonly titleKey: string;
  /** Chave i18n para o corpo da mensagem */
  readonly bodyKey: string;
}

export type MetricNoticeProps = MetricNoticeByIdProps | MetricNoticeDirectProps;

// ---------------------------------------------------------------------------
// Mapa de icone por kind
// ---------------------------------------------------------------------------

const ICON_MAP: Record<MetricNoticeKind, string> = {
  discontinued: 'update',
  updated: 'info',
};

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function MetricNotice(props: MetricNoticeProps): JSX.Element | null {
  let kind: MetricNoticeKind;
  let titleKey: string;
  let bodyKey: string;

  if (props.id !== undefined) {
    const entry = metricNotices[props.id];
    if (entry === undefined) {
      // Id nao registrado: nao renderiza nada (falha silenciosa em prod)
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`[MetricNotice] id nao registrado em metric-notices.ts: "${props.id}"`);
      }
      return null;
    }
    kind = entry.kind;
    titleKey = entry.titleKey;
    bodyKey = entry.bodyKey;
  } else {
    kind = props.kind;
    titleKey = props.titleKey;
    bodyKey = props.bodyKey;
  }

  const icon = ICON_MAP[kind];

  // t() aceita apenas TranslationKey — cast necessario porque titleKey/bodyKey
  // sao strings dinamicas (vindas do registro). O registro e de responsabilidade
  // do engenheiro que adiciona entradas; o tipo nao pode ser inferido em runtime.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const title = t(titleKey as any);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const body = t(bodyKey as any);

  return (
    <div className="metric-notice metric-notice--info" role="note">
      <span className="material-symbols-rounded metric-notice__icon" aria-hidden="true">
        {icon}
      </span>
      <div className="metric-notice__content">
        <p className="metric-notice__title">{title}</p>
        <p className="metric-notice__body">{body}</p>
      </div>
    </div>
  );
}
